import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { useSearchParams } from "react-router-dom";
import SortIcon from "../ui-icons/table/SortIcon";
import { useAppSelector } from "src/hooks/redux";
import { selectCurrentUser } from "src/modules/users/selector";
import { twMerge } from "tailwind-merge";
import { TooltipItem } from "src/utils/Tooltip";
import { updateItemRequest } from "src/modules/spItems/api";
import { FiveWhyType } from "src/api/config/systemSwitch";
import { myTasks5WayDependListName, myTasksDeviationReportDependListName } from "src/api/config/myTasksLists.config";
import LoadingOpBg from "../LoadingOpBg";
import { DrawerComponent } from "../DialogComponent";
import Form from "../Form";
import TriggerCard from "src/pages/DashboardPage/mytasks/trigger/trigger";

const MyTasksTriggersTable = (props: any) => {

    const { data, setScrollDown, filters, type, setRerenderTable } = props;
    const [sortData, setSortData] = useState<any>([]);
    const [query, setQuery] = useSearchParams();
    const currentUser: any = useAppSelector((state) => selectCurrentUser(state));
    const ITEMS_PER_PAGE = 100;
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
    const [customLoading, setCustomLoading] = useState(false);
    const [openCardTrigger, setOpenCardTrigger] = useState(false);
    const [checkedTriggerCard, setCheckedTriggerCard] = useState<any>(null);
    const [formData, setFormData] = useState<any>(null);
    const [inArchiveCardTrigger, setInArchiveCardTrigger] = useState(false);

    const [tableHeaderContent, setTableHeaderContent] = useState<any>({
        LocationStr: {
            title: "Локация",
            sort: true,
            style: { width: "13%" },
            sortDirection: "asc",
            sortType: "string"
        },
        DepartmentStr: {
            title: "Отдел",
            sort: true,
            style: { width: "15%" },
            sortDirection: "default",
            sortType: "string"
        },
        RegionStr: {
            title: "Участок",
            sort: true,
            style: { width: "15%" },
            sortDirection: "default",
            sortType: "string"
        },
        TriggerStr: {
            title: "Триггер",
            sort: true,
            style: { width: "13%" },
            sortDirection: "default",
            sortType: "string"
        },
        KpiStr: {
            title: "KPI",
            sort: true,
            style: { width: "10%" },
            sortDirection: "default",
            sortType: "string"
        },
        StatusStr: {
            title: "Статус",
            sort: false,
            style: { width: "13%" },
            sortDirection: "default",
            sortType: "string"
        },
    });

    const sortDataHandle = (sortField: string, sortDirection: string, sortType: string) => {
        const tempTableHeaderContent = { ...tableHeaderContent };
        const tempData = [...data.map((item: any) => { return { ...item, ...{ LocationStr: item.Location.get_lookupValue(), DepartmentStr: item.Departament.get_lookupValue(), RegionStr: item.Region.get_lookupValue(), TriggerStr: item.Trigger.get_lookupValue(), KpiStr: item.Kpi && item.Kpi.get_lookupValue() || '-', StatusStr: item.Status } } })];
        Object.keys(tempTableHeaderContent).map((key: string) => {
            tempTableHeaderContent[key]['sortDirection'] = 'default';
        });
        if (sortDirection === 'default') {
            tempTableHeaderContent[sortField]['sortDirection'] = 'desc';
        }
        if (sortDirection === 'desc') {
            tempTableHeaderContent[sortField]['sortDirection'] = 'asc';
        }
        if (sortDirection === 'asc') {
            tempTableHeaderContent[sortField]['sortDirection'] = 'default';
            setSortData([...data]);
        }
        if (sortType === 'number') {
            if (tempTableHeaderContent[sortField]['sortDirection'] === 'desc') {
                tempData.sort((a: any, b: any) => parseFloat(a[sortField]) - parseFloat(b[sortField]));
            }
            if (tempTableHeaderContent[sortField]['sortDirection'] === 'asc') {
                tempData.sort((a: any, b: any) => parseFloat(b[sortField]) - parseFloat(a[sortField]));
            }
        }
        if (sortType === 'string') {
            if (tempTableHeaderContent[sortField]['sortDirection'] === 'desc') {
                tempData.sort((a: any, b: any) => {
                    return a[sortField].localeCompare(b[sortField]);
                });
            }
            if (tempTableHeaderContent[sortField]['sortDirection'] === 'asc') {
                tempData.sort((a: any, b: any) => {
                    return b[sortField].localeCompare(a[sortField]);
                });
            }
        }
        if (sortType === 'date') {
            if (tempTableHeaderContent[sortField]['sortDirection'] === 'desc') {
                const distantFuture = new Date(8640000000000000);
                tempData.sort((a: any, b: any) => {
                    let dateA = a[sortField] || distantFuture;
                    let dateB = b[sortField] || distantFuture;
                    return dateA.getTime() - dateB.getTime();
                });
            }
            if (tempTableHeaderContent[sortField]['sortDirection'] === 'asc') {
                const distantFuture = new Date(8640000000000000);
                tempData.sort((a: any, b: any) => {
                    let dateA = a[sortField] || distantFuture;
                    let dateB = b[sortField] || distantFuture;
                    return dateB.getTime() - dateA.getTime();
                });
            }
        }
        setTableHeaderContent({ ...tempTableHeaderContent });
        setSortData([...tempData]);
    }

    useEffect(() => {
        setSortData([...data]);
        sortDataHandle('LocationStr', 'default', 'string');
    }, [data]);

    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        // Триггерим за 100px до конца для плавности
        if (scrollHeight - scrollTop - clientHeight < 100) {
            setScrollDown(true);
            setVisibleCount(prev => {
                const newCount = prev + ITEMS_PER_PAGE;
                return newCount >= sortData.length ? sortData.length : newCount;
            });
        }
    }, [sortData.length]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    useEffect(() => {
        setVisibleCount(ITEMS_PER_PAGE);
        setSelectedItems(new Set());
    }, [filters]);

    // Выбрать / снять один элемент
    const handleSelectItem = (
        event: React.ChangeEvent<HTMLInputElement>,
        item: any
    ) => {
        event.stopPropagation();

        setSelectedItems(prev => {
            const newSelectedItems = new Set(prev);

            if (event.target.checked) {
                newSelectedItems.add(item.ID);
            } else {
                newSelectedItems.delete(item.ID);
            }
            return newSelectedItems;
        });
    };

    // Выбрать / снять все элементы
    const handleSelectAll = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const checked = event.target.checked;

        if (checked) {
            setSelectedItems(
                new Set(sortData.map((item: any) => item.ID))
            );
        } else {
            setSelectedItems(new Set());
        }
    };

    // Общая функция архивирования / разархивирования
    const handleArchiveToggle = () => {
        setCustomLoading(true);
        const selectedItemsData = sortData.filter(
            (item: any) => selectedItems.has(item.ID)
        );
        const updateTriggersIds = Array.from(selectedItemsData).map((item: any) => {
            return { ID: item.ID, Hide: !item.Hide }
        });
        updateItemRequest({ listName: type === FiveWhyType && myTasks5WayDependListName || myTasksDeviationReportDependListName, body: updateTriggersIds[0], bodyArr: updateTriggersIds }).then(() => {
            setSelectedItems(new Set());
            setRerenderTable(false);
            setCustomLoading(false);
        });
    };

    // Для одного элемента функция архивирования / разархивирования
    const handleItemArchiveToggle = (
        item: any,
        event?: React.MouseEvent
    ) => {
        event && event.preventDefault();
        event && event.stopPropagation();
        setCustomLoading(true);
        const updateData = { ID: item.ID, Hide: !item.Hide }
        updateItemRequest({ listName: type === FiveWhyType && myTasks5WayDependListName || myTasksDeviationReportDependListName, body: updateData }).then(() => {
            setSelectedItems(new Set());
            setRerenderTable(false);
            setCustomLoading(false);
        });
    };

    const allItemsSelected =
        sortData.length > 0 &&
        sortData.every((item: any) => selectedItems.has(item.ID));

    const someItemsSelected =
        sortData.some((item: any) => selectedItems.has(item.ID));

    const canUseFunctionality = (
        locationIds: number | number[] | undefined
    ) => {
        // Администратор имеет доступ ко всему
        if (currentUser?.isAdmin) {
            return true;
        }

        // Все доступные пользователю локации
        const userLocationIds = [
            ...(currentUser?.isLocationEditorMyTasks || []),
            ...(currentUser?.isLocationAdminMyTasks || []),
        ].map(String);

        // Приводим входные локации к массиву
        const locations = Array.isArray(locationIds)
            ? locationIds
            : locationIds !== undefined
                ? [locationIds]
                : [];

        if (!locations.length) {
            return false;
        }

        // Пользователь должен иметь доступ ко всем локациям
        return locations.every((locationId) =>
            userLocationIds.includes(String(locationId))
        );
    };

    useEffect(() => {
        if (!openCardTrigger) {
            setCheckedTriggerCard(null);
        }
    }, [openCardTrigger]);

    useEffect(() => {
        if (inArchiveCardTrigger) {
            handleItemArchiveToggle(checkedTriggerCard);
            setInArchiveCardTrigger(false);
            setOpenCardTrigger(false);
        }
    }, [inArchiveCardTrigger]);

    return (
        <FlexCol className="w-full mt-5">
            <FlexCol className="relative">
                {customLoading && <LoadingOpBg />}
                <div ref={scrollContainerRef} id="owd_calendar_wrapper" className="relative pb-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB] max-h-[calc(100vh-160px)] min-h-[170px]">
                    <FlexRow className="pb-5 sticky-custom max-h-[90px] bg-white pt-5 px-10 gap-5 z-10 w-full min-w-[1100px]">
                        {canUseFunctionality(filters.Location) &&
                            <FlexCol
                                className="flex-shrink-0"
                                style={{ width: "2%" }}
                            >
                                <label
                                    className={twMerge("flex cursor-pointer select-none text-black mb-2")}
                                >
                                    <div className="relative">
                                        <input type="checkbox" checked={allItemsSelected} onChange={handleSelectAll} className="sr-only" />
                                        <div
                                            className={twMerge(
                                                "flex items-center justify-center w-5 h-5 mr-4 border rounded box border-stroke",
                                                allItemsSelected && "border-[#012169]" || "border-[#637381]",
                                            )}
                                        >
                                            <span className={twMerge(allItemsSelected && "opacity-100" || "opacity-0")}>
                                                <svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                                                        fill="#012169"
                                                        stroke="#012169"
                                                        strokeWidth="0.4"
                                                    ></path>
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                </label>
                            </FlexCol> ||
                            <FlexCol
                                className="flex-shrink-0"
                                style={{ width: "2%" }}
                            ><></></FlexCol>
                        }
                        {Object.keys(tableHeaderContent).map((fieldKey) => {
                            const key: string = fieldKey;
                            return (
                                <TableHeaderColumn
                                    key={fieldKey}
                                    sortField={fieldKey}
                                    tableHeaderContent={tableHeaderContent[key]}
                                    sortDataHandle={sortDataHandle}
                                />
                            );
                        })}
                        {canUseFunctionality(filters.Location) &&
                            <>
                                {filters['Hide'] === true &&
                                    <FlexCol
                                        className="flex-shrink-0"
                                        style={{ width: "1%" }}
                                    >
                                        <TooltipItem position="left" tooltipsText={`Разархивировать (${selectedItems.size})`}>
                                            <a
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (selectedItems.size > 0) {
                                                        handleArchiveToggle();
                                                    }
                                                }}
                                                className="flex w-6 h-6 text-[#9CA3AF] hover:text-[#012169]"
                                            >
                                                {selectedItems.size > 0 &&
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M2.46875 1.52051H7.96875C8.48145 1.52051 8.94302 1.77311 9.24512 2.19043L9.36426 2.37891L10.0176 3.62402L10.1113 3.80176H17.5312C18.4168 3.80176 19.1357 4.50362 19.1357 5.375V16.9062C19.1357 17.7847 18.4409 18.4795 17.5625 18.4795H2.46875C1.59035 18.4795 0.895508 17.7847 0.895508 16.9062V3.09375C0.895508 2.21534 1.59034 1.52051 2.46875 1.52051ZM2.46875 2.26074C2.00341 2.26074 1.63574 2.6284 1.63574 3.09375V16.9062C1.63574 17.3716 2.0034 17.7393 2.46875 17.7393H17.5625C18.0278 17.7393 18.3955 17.3716 18.3955 16.9062V5.375C18.3955 4.90966 18.0278 4.54199 17.5625 4.54199H9.90625C9.78752 4.54199 9.64503 4.46096 9.57324 4.33984L8.69922 2.7168C8.55034 2.42674 8.28418 2.26074 7.96875 2.26074H2.46875Z" fill="#012169" stroke="#012169" stroke-width="0.666667" />
                                                        <path d="M10.5 7.625C10.2187 7.34375 9.78125 7.34375 9.5 7.625L6.78125 10.3125C6.5 10.5938 6.5 11.0313 6.78125 11.3125C7.0625 11.5938 7.5 11.5938 7.78125 11.3125L9.3125 9.8125V14.375C9.3125 14.75 9.625 15.0937 10.0312 15.0937C10.4375 15.0937 10.7187 14.7812 10.7187 14.375V9.78125L12.2812 11.3125C12.4062 11.4375 12.5937 11.5 12.7812 11.5C12.9687 11.5 13.1562 11.4375 13.2812 11.2812C13.5625 11 13.5625 10.5625 13.2812 10.2813L10.5 7.625Z" fill="#012169" />
                                                    </svg> ||
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M2.46875 1.52051H7.96875C8.48145 1.52051 8.94302 1.77311 9.24512 2.19043L9.36426 2.37891L10.0176 3.62402L10.1113 3.80176H17.5312C18.4168 3.80176 19.1357 4.50362 19.1357 5.375V16.9062C19.1357 17.7847 18.4409 18.4795 17.5625 18.4795H2.46875C1.59035 18.4795 0.895508 17.7847 0.895508 16.9062V3.09375C0.895508 2.21534 1.59034 1.52051 2.46875 1.52051ZM2.46875 2.26074C2.00341 2.26074 1.63574 2.6284 1.63574 3.09375V16.9062C1.63574 17.3716 2.0034 17.7393 2.46875 17.7393H17.5625C18.0278 17.7393 18.3955 17.3716 18.3955 16.9062V5.375C18.3955 4.90966 18.0278 4.54199 17.5625 4.54199H9.90625C9.78752 4.54199 9.64503 4.46096 9.57324 4.33984L8.69922 2.7168C8.55034 2.42674 8.28418 2.26074 7.96875 2.26074H2.46875Z" fill="#9CA3AF" stroke="#9CA3AF" stroke-width="0.666667" />
                                                        <path d="M10.5 7.625C10.2187 7.34375 9.78125 7.34375 9.5 7.625L6.78125 10.3125C6.5 10.5938 6.5 11.0313 6.78125 11.3125C7.0625 11.5938 7.5 11.5938 7.78125 11.3125L9.3125 9.8125V14.375C9.3125 14.75 9.625 15.0937 10.0312 15.0937C10.4375 15.0937 10.7187 14.7812 10.7187 14.375V9.78125L12.2812 11.3125C12.4062 11.4375 12.5937 11.5 12.7812 11.5C12.9687 11.5 13.1562 11.4375 13.2812 11.2812C13.5625 11 13.5625 10.5625 13.2812 10.2813L10.5 7.625Z" fill="#9CA3AF" />
                                                    </svg>
                                                }
                                            </a>
                                        </TooltipItem>
                                    </FlexCol>
                                    ||
                                    <FlexCol
                                        className="flex-shrink-0"
                                        style={{ width: "1%" }}
                                    >
                                        <TooltipItem position="left" tooltipsText={`Архивировать (${selectedItems.size})`}>
                                            <a
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (selectedItems.size > 0) {
                                                        handleArchiveToggle();
                                                    }
                                                }}
                                                className="flex w-6 h-6 text-[#9CA3AF] hover:text-[#012169]"
                                            >
                                                {selectedItems.size > 0 &&
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M2.46875 1.52051H7.96875C8.48145 1.52051 8.94302 1.77311 9.24512 2.19043L9.36426 2.37891L10.0176 3.62402L10.1113 3.80176H17.5312C18.4168 3.80176 19.1357 4.50362 19.1357 5.375V16.9062C19.1357 17.7847 18.4409 18.4795 17.5625 18.4795H2.46875C1.59035 18.4795 0.895508 17.7847 0.895508 16.9062V3.09375C0.895508 2.21534 1.59034 1.52051 2.46875 1.52051ZM2.46875 2.26074C2.00341 2.26074 1.63574 2.6284 1.63574 3.09375V16.9062C1.63574 17.3716 2.0034 17.7393 2.46875 17.7393H17.5625C18.0278 17.7393 18.3955 17.3716 18.3955 16.9062V5.375C18.3955 4.90966 18.0278 4.54199 17.5625 4.54199H9.90625C9.78752 4.54199 9.64503 4.46096 9.57324 4.33984L8.69922 2.7168C8.55034 2.42674 8.28418 2.26074 7.96875 2.26074H2.46875Z" fill="#012169" stroke="#012169" stroke-width="0.666667" />
                                                        <path d="M12.25 11.1875L10.7187 12.6875V8.125C10.7187 7.75 10.4062 7.40625 10 7.40625C9.59375 7.40625 9.28125 7.71875 9.28125 8.125V12.7188L7.71875 11.1875C7.4375 10.9062 7 10.9062 6.71875 11.1875C6.4375 11.4687 6.4375 11.9062 6.71875 12.1875L9.46875 14.875C9.59375 15 9.78125 15.0625 9.96875 15.0625C10.1562 15.0625 10.3125 15 10.4687 14.875L13.2187 12.1875C13.5 11.9062 13.5 11.4687 13.2187 11.1875C12.9687 10.9062 12.5312 10.9062 12.25 11.1875Z" fill="#012169" />
                                                    </svg> ||
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M2.46875 1.52051H7.96875C8.48145 1.52051 8.94302 1.77311 9.24512 2.19043L9.36426 2.37891L10.0176 3.62402L10.1113 3.80176H17.5312C18.4168 3.80176 19.1357 4.50362 19.1357 5.375V16.9062C19.1357 17.7847 18.4409 18.4795 17.5625 18.4795H2.46875C1.59035 18.4795 0.895508 17.7847 0.895508 16.9062V3.09375C0.895508 2.21534 1.59034 1.52051 2.46875 1.52051ZM2.46875 2.26074C2.00341 2.26074 1.63574 2.6284 1.63574 3.09375V16.9062C1.63574 17.3716 2.0034 17.7393 2.46875 17.7393H17.5625C18.0278 17.7393 18.3955 17.3716 18.3955 16.9062V5.375C18.3955 4.90966 18.0278 4.54199 17.5625 4.54199H9.90625C9.78752 4.54199 9.64503 4.46096 9.57324 4.33984L8.69922 2.7168C8.55034 2.42674 8.28418 2.26074 7.96875 2.26074H2.46875Z" fill="#9CA3AF" stroke="#9CA3AF" stroke-width="0.666667" />
                                                        <path d="M12.25 11.1875L10.7187 12.6875V8.125C10.7187 7.75 10.4062 7.40625 10 7.40625C9.59375 7.40625 9.28125 7.71875 9.28125 8.125V12.7188L7.71875 11.1875C7.4375 10.9062 7 10.9062 6.71875 11.1875C6.4375 11.4687 6.4375 11.9062 6.71875 12.1875L9.46875 14.875C9.59375 15 9.78125 15.0625 9.96875 15.0625C10.1562 15.0625 10.3125 15 10.4687 14.875L13.2187 12.1875C13.5 11.9062 13.5 11.4687 13.2187 11.1875C12.9687 10.9062 12.5312 10.9062 12.25 11.1875Z" fill="#9CA3AF" />
                                                    </svg>
                                                }
                                            </a>
                                        </TooltipItem>
                                    </FlexCol> ||
                                    <FlexCol
                                        className="flex-shrink-0"
                                        style={{ width: "1%" }}
                                    ><></></FlexCol>
                                }
                            </>
                        }
                    </FlexRow>
                    <FlexCol className="divide-y grow px-10 w-full min-w-[1100px]">
                        {sortData.map((item: any, idx: number) => (
                            <FlexRow
                                className="relative py-4 rounded-lg -mx-2 px-2 gap-5 cursor-pointer text-[#637381] hover:bg-[#F3F4F6]"
                                key={item.ID}
                                onClick={() => {
                                    setCheckedTriggerCard(item);
                                    setOpenCardTrigger(true);
                                }}
                            >
                                {/* Чекбокс */}
                                {canUseFunctionality(item.Location.get_lookupId()) &&
                                    <FlexCol
                                        className="flex-shrink-0"
                                        style={{ width: "2%" }}
                                    >
                                        <label
                                            className={twMerge("flex cursor-pointer select-none text-black mb-2")}
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            <div className="relative">
                                                <input type="checkbox" checked={selectedItems.has(item.ID)} onChange={(event) => handleSelectItem(event, item)} className="sr-only" />
                                                <div
                                                    className={twMerge(
                                                        "flex items-center justify-center w-5 h-5 mr-4 border rounded box border-stroke",
                                                        selectedItems.has(item.ID) && "border-[#012169]" || "border-[#637381]",
                                                    )}
                                                >
                                                    <span className={twMerge(selectedItems.has(item.ID) && "opacity-100" || "opacity-0")}>
                                                        <svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path
                                                                d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                                                                fill="#012169"
                                                                stroke="#012169"
                                                                strokeWidth="0.4"
                                                            ></path>
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                        </label>
                                    </FlexCol> ||
                                    <FlexCol
                                        className="flex-shrink-0"
                                        style={{ width: "2%" }}
                                    ><></></FlexCol>
                                }
                                <FlexCol style={{ width: tableHeaderContent.LocationStr.style.width }} className="break-words">
                                    <span className="block text-sm font-normal not-italic">{item.Location.get_lookupValue()}</span>
                                </FlexCol>
                                <FlexCol style={{ width: tableHeaderContent.DepartmentStr.style.width }} className="break-words">
                                    <span className="block text-sm font-normal not-italic">{item.Departament.get_lookupValue()}</span>
                                </FlexCol>
                                <FlexCol style={{ width: tableHeaderContent.RegionStr.style.width }} className="break-words">
                                    <span className="block text-sm font-normal not-italic">{item.Region.get_lookupValue()}</span>
                                </FlexCol>
                                <FlexCol style={{ width: tableHeaderContent.TriggerStr.style.width }} className="break-words">
                                    <span className="block text-sm font-normal not-italic">{item.Trigger.get_lookupValue()}</span>
                                </FlexCol>
                                <FlexCol style={{ width: tableHeaderContent.KpiStr.style.width }} className="break-words">
                                    <span className="block text-sm font-normal not-italic">{item.Kpi && item.Kpi.get_lookupValue() || '-'}</span>
                                </FlexCol>
                                <FlexCol style={{ width: tableHeaderContent.StatusStr.style.width }} className="break-words">
                                    <div className="w-full flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                            <circle cx="5" cy="5" r="5" fill={item.Status === 'Активный' && "#82E6AC" || "#E5E7EB"} />
                                        </svg>
                                        &nbsp;&nbsp;{item.Status}
                                    </div>
                                </FlexCol>
                                {canUseFunctionality(item.Location.get_lookupId()) &&
                                    <FlexCol
                                        style={{ width: '1%' }}
                                        className="break-words"
                                    >
                                        <TooltipItem position="left" tooltipsText={filters['Hide'] === true && `Разархивировать` || `Архивировать`}>
                                            <a
                                                href="#"
                                                onClick={(event) => handleItemArchiveToggle(item, event)}
                                                className="flex w-6 h-6 text-[#9CA3AF] hover:text-[#012169]"
                                            >
                                                {filters['Hide'] === true &&
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M2.46875 1.52051H7.96875C8.48145 1.52051 8.94302 1.77311 9.24512 2.19043L9.36426 2.37891L10.0176 3.62402L10.1113 3.80176H17.5312C18.4168 3.80176 19.1357 4.50362 19.1357 5.375V16.9062C19.1357 17.7847 18.4409 18.4795 17.5625 18.4795H2.46875C1.59035 18.4795 0.895508 17.7847 0.895508 16.9062V3.09375C0.895508 2.21534 1.59034 1.52051 2.46875 1.52051ZM2.46875 2.26074C2.00341 2.26074 1.63574 2.6284 1.63574 3.09375V16.9062C1.63574 17.3716 2.0034 17.7393 2.46875 17.7393H17.5625C18.0278 17.7393 18.3955 17.3716 18.3955 16.9062V5.375C18.3955 4.90966 18.0278 4.54199 17.5625 4.54199H9.90625C9.78752 4.54199 9.64503 4.46096 9.57324 4.33984L8.69922 2.7168C8.55034 2.42674 8.28418 2.26074 7.96875 2.26074H2.46875Z" fill="currentColor" stroke="currentColor" stroke-width="0.666667" />
                                                        <path d="M10.5 7.625C10.2187 7.34375 9.78125 7.34375 9.5 7.625L6.78125 10.3125C6.5 10.5938 6.5 11.0313 6.78125 11.3125C7.0625 11.5938 7.5 11.5938 7.78125 11.3125L9.3125 9.8125V14.375C9.3125 14.75 9.625 15.0937 10.0312 15.0937C10.4375 15.0937 10.7187 14.7812 10.7187 14.375V9.78125L12.2812 11.3125C12.4062 11.4375 12.5937 11.5 12.7812 11.5C12.9687 11.5 13.1562 11.4375 13.2812 11.2812C13.5625 11 13.5625 10.5625 13.2812 10.2813L10.5 7.625Z" fill="currentColor" />
                                                    </svg> ||
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M2.46875 1.52051H7.96875C8.48145 1.52051 8.94302 1.77311 9.24512 2.19043L9.36426 2.37891L10.0176 3.62402L10.1113 3.80176H17.5312C18.4168 3.80176 19.1357 4.50362 19.1357 5.375V16.9062C19.1357 17.7847 18.4409 18.4795 17.5625 18.4795H2.46875C1.59035 18.4795 0.895508 17.7847 0.895508 16.9062V3.09375C0.895508 2.21534 1.59034 1.52051 2.46875 1.52051ZM2.46875 2.26074C2.00341 2.26074 1.63574 2.6284 1.63574 3.09375V16.9062C1.63574 17.3716 2.0034 17.7393 2.46875 17.7393H17.5625C18.0278 17.7393 18.3955 17.3716 18.3955 16.9062V5.375C18.3955 4.90966 18.0278 4.54199 17.5625 4.54199H9.90625C9.78752 4.54199 9.64503 4.46096 9.57324 4.33984L8.69922 2.7168C8.55034 2.42674 8.28418 2.26074 7.96875 2.26074H2.46875Z" fill="currentColor" stroke="currentColor" stroke-width="0.666667" />
                                                        <path d="M12.25 11.1875L10.7187 12.6875V8.125C10.7187 7.75 10.4062 7.40625 10 7.40625C9.59375 7.40625 9.28125 7.71875 9.28125 8.125V12.7188L7.71875 11.1875C7.4375 10.9062 7 10.9062 6.71875 11.1875C6.4375 11.4687 6.4375 11.9062 6.71875 12.1875L9.46875 14.875C9.59375 15 9.78125 15.0625 9.96875 15.0625C10.1562 15.0625 10.3125 15 10.4687 14.875L13.2187 12.1875C13.5 11.9062 13.5 11.4687 13.2187 11.1875C12.9687 10.9062 12.5312 10.9062 12.25 11.1875Z" fill="currentColor" />
                                                    </svg>
                                                }
                                            </a>
                                        </TooltipItem>
                                    </FlexCol> ||
                                    <FlexCol
                                        style={{ width: '1%' }}
                                        className="break-words"
                                    ><></></FlexCol>
                                }
                            </FlexRow>
                        ))}
                    </FlexCol>
                </div>
            </FlexCol>
            {checkedTriggerCard &&
                <TriggerCard zIndex={70} type={type} setRerenderTable={setRerenderTable} open={openCardTrigger} setOpen={setOpenCardTrigger} trigger={checkedTriggerCard} setInArchiveCardTrigger={setInArchiveCardTrigger} activeTrigger={checkedTriggerCard.Hide} />
            }
        </FlexCol>
    );
}

export default MyTasksTriggersTable;

export function TableHeaderColumn({ tableHeaderContent, sortDataHandle, sortField }: any) {
    return (
        <FlexCol className="select-none" style={tableHeaderContent.style}>
            <FlexRow className="gap-x-2">
                {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
                <FlexCol className="font-medium whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
                {tableHeaderContent.sort && (
                    <FlexCol className="w-auto cursor-pointer" onClick={() => sortDataHandle(sortField, tableHeaderContent.sortDirection, tableHeaderContent.sortType)}>
                        <SortIcon sortDirection={tableHeaderContent.sortDirection} />
                    </FlexCol>
                )}
            </FlexRow>
        </FlexCol>
    );
}
